import React, { useMemo, useState, useEffect } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { processInsightsData } from '../utils/insightsProcessor';
import { getTournamentType } from '../utils/dataProcessor';
import './InsightsLineChart.css';

const InsightsLineChart = ({ data, year = '2025', tournaments }) => {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEventSeries, setSelectedEventSeries] = useState('all');

  // Calculate current and previous year
  const currentYear = String(year);
  const previousYear = String(parseInt(currentYear) - 1);

  // Process the data to get insights information
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
          const dataToProcess = Array.isArray(data) ? data : [];
          console.log('Processing insights data with tournaments:', tournaments); 
        const processedData = await processInsightsData(dataToProcess, tournaments);
        setChartData(processedData);
      } catch (error) {
        console.error('Error processing insights data:', error);
        setChartData([]);
      } finally {
        setLoading(false);
      }
    };

    if (Array.isArray(data) && data.length > 0) {
      loadData();
    } else {
      setChartData([]);
      setLoading(false);
    }
  }, [data]);

  // Transform data to group by tournament and show previous/current year as separate areas
  const transformedChartData = useMemo(() => {
    if (!chartData || chartData.length === 0) return [];

    // Group by tournament (combining years)
    const tournamentGroups = {};
    
    chartData.forEach(item => {
      const tournament = item.tournament;
      const itemYear = item.year;
      const insights = item.insights;
      const eventId = item.eventId;
      
      if (!tournamentGroups[tournament]) {
        tournamentGroups[tournament] = {
          tournament: tournament,
          insightsPrevious: 0,
          insightsCurrent: 0,
          hasPreviousData: false,
          hasCurrentData: false,
          eventId: eventId // Preserve EventId for filtering
        };
      }
      
      // Update EventId if we don't have one yet
      if (!tournamentGroups[tournament].eventId && eventId) {
        tournamentGroups[tournament].eventId = eventId;
      }
      
      if (itemYear === previousYear) {
        tournamentGroups[tournament].insightsPrevious = insights;
        tournamentGroups[tournament].hasPreviousData = true;
      } else if (itemYear === currentYear) {
        tournamentGroups[tournament].insightsCurrent = insights;
        tournamentGroups[tournament].hasCurrentData = true;
      }
    });

          // Tournament groups processed

    return Object.values(tournamentGroups);
  }, [chartData, currentYear, previousYear]);

  // Filter data based on selected Event Series
  const filteredChartData = useMemo(() => {
    if (selectedEventSeries === 'all') {
      return transformedChartData;
    }
    
    return transformedChartData.filter(item => {
      // Use EventId from the processed data if available
      const eventSeries = getTournamentType({ 
        Tournament: item.tournament, 
        EventId: item.eventId 
      });
      
      // Debug: Log tournament mapping
      console.log(`🎾 Tournament "${item.tournament}" (EventId: ${item.eventId}) -> Type: "${eventSeries}", Selected: "${selectedEventSeries}"`);
      
      return eventSeries === selectedEventSeries;
    });
  }, [transformedChartData, selectedEventSeries]);

  // Calculate total insights for the header (sum of both years)
  const totalInsights = useMemo(() => {
    return filteredChartData.reduce((sum, item) => sum + (item.insightsPrevious || 0) + (item.insightsCurrent || 0), 0);
  }, [filteredChartData]);

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const tooltipData = payload[0].payload;
      // Use EventId from the processed data
      const eventSeries = getTournamentType({ 
        Tournament: tooltipData.tournament, 
        EventId: tooltipData.eventId 
      }) || 'Unknown';
      
      // Tooltip data processed
      
      return (
        <div className="insights-tooltip">
          <p className="tooltip-tournament"><strong>Tournament:</strong> {tooltipData.tournament}</p>
          <p className="tooltip-insights-2024"><strong>{previousYear} Insights:</strong> {tooltipData.insightsPrevious || 0}</p>
          <p className="tooltip-insights-2025"><strong>{currentYear} Insights:</strong> {tooltipData.insightsCurrent || 0}</p>
          <p className="tooltip-series"><strong>Event Series:</strong> {eventSeries === '1000' ? 'ATP 1000 (Masters)' : eventSeries === '500' ? 'ATP 500' : eventSeries === '250' ? 'ATP 250' : eventSeries === 'WTF' ? 'ATP Finals' : eventSeries === 'NextGen' ? 'NextGen Finals' : eventSeries}</p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="insights-chart-container">
        <div className="loading-message">
          <h3>Tournament Comparison</h3>
          <p>Loading tournament data and fetching dates from ATP Calendar...</p>
        </div>
      </div>
    );
  }

  if (chartData.length === 0) {
    return (
      <div className="insights-chart-container">
        <div className="no-data-message">
          <h3>Tournament Comparison</h3>
          <p>No tournament data available for insights analysis.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="insights-chart-container">
      <div className="chart-header">
        <div className="chart-title-section">
          <h3>Tournament Comparison</h3>
          <div className="chart-controls">
            <select 
              value={selectedEventSeries} 
              onChange={(e) => setSelectedEventSeries(e.target.value)}
              className="event-series-select"
            >
              <option value="all">All Event Series</option>
              <option value="250">250 Series</option>
              <option value="500">500 Series</option>
              <option value="1000">1000 Series (Masters)</option>
            </select>
          </div>
        </div>
        <div className="chart-metrics">
          <div className="metric-card">
            <span className="metric-label">Total Insights</span>
            <span className="metric-value">{totalInsights.toLocaleString()}</span>
          </div>
        </div>
      </div>

      <div className="chart-wrapper">
        {/* Custom Legend positioned at the top */}
        <div className="custom-legend">
          <div className="legend-item">
            <div className="legend-color" style={{ backgroundColor: '#8884d8' }}></div>
            <span className="legend-label">{previousYear}</span>
          </div>
          <div className="legend-item">
            <div className="legend-color" style={{ backgroundColor: '#82ca9d' }}></div>
            <span className="legend-label">{currentYear}</span>
          </div>
        </div>
        
        <ResponsiveContainer width="100%" height={400}>
          <AreaChart
            data={filteredChartData}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 20,
            }}
          >
            <defs>
              <linearGradient id="insightsGradientPrevious" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="#8884d8" stopOpacity={0.1}/>
              </linearGradient>
              <linearGradient id="insightsGradientCurrent" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="#82ca9d" stopOpacity={0.1}/>
              </linearGradient>
            </defs>
            <XAxis
              dataKey="tournament"
              stroke="#FFFFFF"
              fontSize={12}
              angle={-45}
              textAnchor="end"
              height={80}
              tick={{ fill: '#FFFFFF' }}
            />
            <YAxis
              stroke="#666"
              fontSize={12}
              tickFormatter={(value) => value.toLocaleString()}
            />
            <Tooltip content={<CustomTooltip />} />
            <Area
              type="monotone"
              dataKey="insightsPrevious"
              stroke="#8884d8"
              strokeWidth={3}
              fill="url(#insightsGradientPrevious)"
              dot={{ fill: '#8884d8', strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, stroke: '#8884d8', strokeWidth: 2 }}
              name={previousYear}
            />
            <Area
              type="monotone"
              dataKey="insightsCurrent"
              stroke="#82ca9d"
              strokeWidth={3}
              fill="url(#insightsGradientCurrent)"
              dot={{ fill: '#82ca9d', strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, stroke: '#82ca9d', strokeWidth: 2 }}
              name={currentYear}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default InsightsLineChart; 